import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Snackbar, Alert, CircularProgress, SelectChangeEvent } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../components/common/list-page';
import EmptyStateMessage from '../components/common/EmptyStateMessage';

// Import components
import { OrdersActionButtons } from '../components/sales/orders/OrdersActionButtons';
import { OrdersSummaryBar } from '../components/sales/orders/OrdersSummaryBar';
import CommonPagination from '../components/common/Pagination';
import { OrdersTable } from '../components/sales/orders/OrdersTable';
import { OrdersFilters } from '../components/sales/orders/OrdersFilters';

// Import types and services
import {
  SalesOrder,
  SalesOrderFilter,
  ReturnEligibilityResponse,
  ReturnFormData,
  RefundSummary,
} from '../types/sales.types';
import { salesService } from '../services/sales.service';
import { refetchCachedSaleFulfillmentDetails } from '../services/saleFulfillmentDetailCache';
import { LocationsService, SalesOrderResponseDto } from '../api';
import { selectCurrentUser } from '../redux/slices/auth.slice';
import { useCanChangeInventoryLocation } from '../hooks/useCanChangeInventoryLocation';

// Import hooks and modals
import { useSalesModal } from '../hooks/useSalesModal';
import SalesModal from '../components/sales/SalesModal';
import { OrderDetailsModal } from '../components/sales/orders/OrderDetailsModal';
import { PaymentModal } from '../components/sales/orders/PaymentModal';
import { InstallmentsModal } from '../components/sales/orders/InstallmentsModal';
import { ReturnOrderModal } from '../components/sales/orders/ReturnOrderModal';
import { ReturnConfirmationModal } from '../components/sales/orders/ReturnConfirmationModal';
import { ExchangeDrawer } from '../components/sales/orders/ExchangeModal';
import { EditSaleDateModal } from '../components/sales/orders/EditSaleDateModal';
import { useEditSaleDate } from '../hooks/useEditSaleDate';
import { SALES_ORDER_MESSAGES } from '../utils/constants';
import { canExchangeSalesOrder } from '../utils/sales/sales-order-actions';
import { filterBranchLocations } from '../components/common/list-page';

interface PaginationState {
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
}

const SalesOrdersPage: React.FC = () => {
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser) as {
    primaryLocationId?: number | null;
  } | null;
  const canChangeInventoryLocation = useCanChangeInventoryLocation();
  const isLocationLocked = Boolean(currentUser?.primaryLocationId) && !canChangeInventoryLocation;

  // State for filters
  const [filters, setFilters] = useState<SalesOrderFilter>({
    orderCode: '',
    branchReceiptCode: '',
    customerSearchTerm: '',
    customerPhone: '',
    status: '',
    locationId:
      isLocationLocked && currentUser?.primaryLocationId
        ? currentUser.primaryLocationId
        : undefined,
    customerId: undefined,
    startDate: '',
    endDate: '',
  });

  // Mantener alcance en sucursal primaria cuando está bloqueado
  useEffect(() => {
    if (!isLocationLocked || !currentUser?.primaryLocationId) return;
    const primaryId = currentUser.primaryLocationId;
    setFilters((prev) =>
      prev.locationId === primaryId ? prev : { ...prev, locationId: primaryId },
    );
  }, [isLocationLocked, currentUser?.primaryLocationId]);

  // State for pagination
  const [pagination, setPagination] = useState<PaginationState>({
    itemsPerPage: 10,
    currentPage: 1,
    totalItems: 0,
  });

  // State for selected orders
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  /** Expanded mixed-sale rows — ref keeps list fetch deps stable while refetch scopes to these IDs. */
  const [expandedOrderIds, setExpandedOrderIds] = useState<number[]>([]);
  const expandedOrderIdsRef = useRef(expandedOrderIds);
  expandedOrderIdsRef.current = expandedOrderIds;

  // State for data
  const [orders, setOrders] = useState<SalesOrderResponseDto[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [postSalesCounts, setPostSalesCounts] = useState<{
    total: number;
    pendientesDePago: number;
    completadas: number;
    devueltas: number;
    intercambios: number;
  } | null>(null);
  const [postSalesLoading, setPostSalesLoading] = useState(false);
  // State for loading and errors
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Note: Backend automatically gets user ID from JWT token

  // Sales modal hook
  const { openModal, setOnOrderCreated } = useSalesModal();

  // Modal states
  const [orderDetailsModal, setOrderDetailsModal] = useState<{
    open: boolean;
    orderId: number | null;
  }>({
    open: false,
    orderId: null,
  });

  // Refresh trigger for order details modal
  const [orderDetailsRefreshTrigger, setOrderDetailsRefreshTrigger] = useState<number>(0);

  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    order: SalesOrder | null;
  }>({
    open: false,
    order: null,
  });

  // ⚠️ INACTIVO: InstallmentsModal existe pero no se puede abrir desde la UI
  // No hay botón en OrderDetailsModal que active este modal
  // TODO: Implementar botón para generar/ver cuotas si se necesita esta funcionalidad
  // Histórico de movimientos: sin eventos mientras cuotas esté oculto; al activar,
  // registrar create/generate/pay/cancel en product_movement_history (backend).
  const [installmentsModal, setInstallmentsModal] = useState<{
    open: boolean;
    order: SalesOrder | null;
  }>({
    open: false,
    order: null,
  });

  // Return modal state
  const [returnModal, setReturnModal] = useState<{
    open: boolean;
    orderId: number | null;
    orderCode: string | null;
    eligibility: ReturnEligibilityResponse | null;
  }>({
    open: false,
    orderId: null,
    orderCode: null,
    eligibility: null,
  });

  const [returnConfirmationModal, setReturnConfirmationModal] = useState<{
    open: boolean;
    refundSummary: RefundSummary | null;
  }>({
    open: false,
    refundSummary: null,
  });

  // Exchange modal state
  const [exchangeModal, setExchangeModal] = useState<{
    open: boolean;
    order: SalesOrder | null;
  }>({
    open: false,
    order: null,
  });

  const [editSaleDateModal, setEditSaleDateModal] = useState<{
    open: boolean;
    order: SalesOrderResponseDto | null;
  }>({
    open: false,
    order: null,
  });

  // Fetch orders with backend filtering
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);

    try {
      // Prepare filter parameters for backend
      const filterParams: any = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      };

      // Add filters only if they have values
      if (filters.orderCode?.trim()) {
        filterParams.orderCode = filters.orderCode.trim();
      }
      if (filters.branchReceiptCode?.trim()) {
        filterParams.branchReceiptCode = filters.branchReceiptCode.trim();
      }
      if (filters.customerSearchTerm?.trim()) {
        filterParams.customerSearchTerm = filters.customerSearchTerm.trim();
      }
      if (filters.customerPhone?.trim()) {
        filterParams.customerPhone = filters.customerPhone.trim();
      }
      if (filters.status && filters.status !== '') {
        filterParams.status = filters.status;
      }
      if (filters.locationId) {
        filterParams.locationId = filters.locationId;
      }
      if (filters.customerId) {
        filterParams.customerId = filters.customerId;
      }
      if (filters.startDate) {
        filterParams.startDate = filters.startDate;
      }
      if (filters.endDate) {
        filterParams.endDate = filters.endDate;
      }

      // Use the new filtered endpoint
      const response = await salesService.getSalesOrdersWithFilters(filterParams);

      setOrders(response.data);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.pagination.total,
      }));

      // Keep expanded SOL/TRL timelines in sync with list refresh (shared cache).
      void refetchCachedSaleFulfillmentDetails(expandedOrderIdsRef.current);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      setFetchError('No se pudieron cargar las ventas. Intente de nuevo.');
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // Fetch post-sales counts
  const fetchPostSalesCounts = useCallback(async () => {
    setPostSalesLoading(true);
    try {
      const counts = await salesService.getPostSalesCounts(filters.locationId);
      setPostSalesCounts(counts);
    } catch (error) {
      console.error('Error fetching post-sales counts:', error);
    } finally {
      setPostSalesLoading(false);
    }
  }, [filters.locationId]);

  // Re-run list + KPIs with current applied filters (Buscar semantics; does not clear filters).
  const refreshOrdersAfterMutation = useCallback(() => {
    void fetchOrders();
    void fetchPostSalesCounts();
  }, [fetchOrders, fetchPostSalesCounts]);

  const handleSaleDateSavedFromTable = useCallback(
    async (orderId: number) => {
      setEditSaleDateModal({ open: false, order: null });
      refreshOrdersAfterMutation();
      if (orderDetailsModal.open && orderDetailsModal.orderId === orderId) {
        setOrderDetailsRefreshTrigger((prev) => prev + 1);
      }
      setNotification({
        open: true,
        message: 'Fecha de venta actualizada',
        severity: 'success',
      });
    },
    [refreshOrdersAfterMutation, orderDetailsModal.open, orderDetailsModal.orderId],
  );

  const editSaleDateAction = useEditSaleDate(handleSaleDateSavedFromTable);

  // Set up callback to reload orders when a new sale is completed
  useEffect(() => {
    setOnOrderCreated(() => {
      refreshOrdersAfterMutation();
    });
  }, [setOnOrderCreated, refreshOrdersAfterMutation]);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const response = await LocationsService.locationsControllerFindAll();
      if (response && response.data && Array.isArray(response.data)) {
        setLocations(response.data);
      } else if (Array.isArray(response)) {
        setLocations(response);
      } else {
        console.warn('Unexpected locations response format:', response);
        setLocations([]);
      }
    } catch (error: any) {
      console.error('Error fetching locations:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.LOAD_BRANCHES_ERROR,
      });
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Fetch orders when filters or pagination changes
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Fetch post-sales counts on mount and when location filter changes
  useEffect(() => {
    fetchPostSalesCounts();
  }, [fetchPostSalesCounts]);

  // Deep-link: abrir detalle sin resetear filtros/paginación del workspace
  useEffect(() => {
    const state = location.state as { openOrderDetails?: number } | null;
    if (state?.openOrderDetails) {
      setOrderDetailsModal({
        open: true,
        orderId: state.openOrderDetails,
      });

      // Limpiar el estado para evitar reabrir el modal en navegaciones futuras
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Event handlers
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // fetchOrders will be called automatically by useEffect when pagination changes
  };

  const handleReset = () => {
    // OrdersFilters ya reinicia los filtros (preservando alcance de sucursal)
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      totalItems: 0,
    }));
  };

  const branchLocations = useMemo(() => filterBranchLocations(locations), [locations]);

  const handleLocationScopeChange = useCallback(
    (payload: { locationId: string; locationName: string }) => {
      setNotification({
        open: true,
        message: `Mostrando ventas de ${payload.locationName}`,
        severity: 'info',
      });
    },
    [],
  );

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<unknown>) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: parseInt(event.target.value as string),
      currentPage: 1,
    }));
  };

  // Action handlers
  const handleCreateOrder = () => {
    openModal();
  };

  const handleExport = () => {
    setNotification({
      open: true,
      ...SALES_ORDER_MESSAGES.EXPORT_NOT_IMPLEMENTED,
    });
  };

  // Order action handlers
  const handleViewOrder = async (orderId: number) => {
    setOrderDetailsModal({
      open: true,
      orderId: orderId,
    });
  };

  const handleOpenEditSaleDate = (order: SalesOrderResponseDto) => {
    editSaleDateAction.clearError();
    setEditSaleDateModal({
      open: true,
      order,
    });
  };

  const handleCloseEditSaleDate = () => {
    if (editSaleDateAction.loading) return;
    editSaleDateAction.clearError();
    setEditSaleDateModal({
      open: false,
      order: null,
    });
  };

  const handleSaveEditSaleDate = async (saleDateIso: string) => {
    const order = editSaleDateModal.order;
    if (order == null) return;
    await editSaleDateAction.save(order.id, saleDateIso);
  };

  const handleEditOrder = async (orderId: number) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const order = await salesService.getSalesOrderById(orderId);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.EDIT_NOT_IMPLEMENTED,
      });
      // TODO: Implement order editing
    } catch (error) {
      console.error('Error fetching order for edit:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.LOAD_ORDER_EDIT_ERROR,
      });
    }
  };

  const handleDeliverOrder = async (_orderId: number) => {
    try {
      // TODO: Implement deliver order endpoint call
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.DELIVERY_NOT_IMPLEMENTED,
      });
    } catch (error) {
      console.error('Error delivering order:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.MARK_DELIVERED_ERROR,
      });
    }
  };

  // Reimpresión de comprobante desde historial: deshabilitada (UI oculta). Reservado para futuro.
  // const handlePrintReceipt = async (orderId: number) => { ... }

  const handleProcessPayment = async (orderId: number) => {
    try {
      const order = await salesService.getSalesOrderById(orderId);
      setPaymentModal({
        open: true,
        order: order,
      });
    } catch (error) {
      console.error('Error fetching order for payment:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.LOAD_ORDER_PAYMENT_ERROR,
      });
    }
  };

  const handleReturnOrder = async (orderId: number, orderCode?: string) => {
    try {
      setIsLoading(true);

      // Fetch the actual return eligibility to get refundable amounts
      const eligibilityData = await salesService.checkReturnEligibility(orderId);

      setReturnModal({
        open: true,
        orderId: orderId,
        orderCode: orderCode ?? null,
        eligibility: eligibilityData,
      });
    } catch (error: any) {
      console.error('Error opening return modal:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.OPEN_RETURN_ERROR(error.message),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExchangeOrder = async (orderId: number) => {
    try {
      setIsLoading(true);
      const order = await salesService.getSalesOrderComplete(orderId);
      if (!canExchangeSalesOrder(order.status, order.fulfillmentStatus, order.deliveryStatus)) {
        setNotification({
          open: true,
          severity: 'warning',
          message:
            'Esta orden no puede intercambiarse en su estado actual (pago, envío o entrega).',
        });
        return;
      }
      setExchangeModal({
        open: true,
        order: order,
      });
    } catch (error: any) {
      console.error('Error opening exchange modal:', error);
      setNotification({
        open: true,
        severity: 'error',
        message: `Error al abrir el modal de intercambio: ${error.message || 'Error desconocido'}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Modal handlers
  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, orderId: null });
  };

  const handleClosePaymentModal = () => {
    setPaymentModal({ open: false, order: null });
  };

  const handleCloseInstallmentsModal = () => {
    setInstallmentsModal({ open: false, order: null });
  };

  const handleOpenInstallments = async (orderId: number) => {
    try {
      const order = await salesService.getSalesOrderById(orderId);
      setInstallmentsModal({
        open: true,
        order: order,
      });
    } catch (error) {
      console.error('Error fetching order for installments:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.LOAD_INSTALLMENTS_ERROR,
      });
    }
  };

  const handlePaymentProcessed = () => {
    setNotification({
      open: true,
      ...SALES_ORDER_MESSAGES.PAYMENT_PROCESSED_SUCCESS,
    });
    refreshOrdersAfterMutation();
    setPaymentModal({ open: false, order: null });
    setOrderDetailsModal({ open: false, orderId: null });
  };

  const handleInstallmentsUpdated = () => {
    setNotification({
      open: true,
      ...SALES_ORDER_MESSAGES.INSTALLMENTS_UPDATED_SUCCESS,
    });
    refreshOrdersAfterMutation();
  };

  const handleProcessReturn = async (returnData: ReturnFormData) => {
    if (!returnModal.orderId) return;

    try {
      setIsLoading(true);

      const result = await salesService.returnFullOrder(returnModal.orderId, returnData);

      // Show success confirmation with refund summary
      setReturnConfirmationModal({
        open: true,
        refundSummary: result.refundSummary,
      });

      // Close return modal and refresh orders
      setReturnModal({ open: false, orderId: null, orderCode: null, eligibility: null });
      refreshOrdersAfterMutation();
    } catch (error: any) {
      console.error('Error processing return:', error);
      setNotification({
        open: true,
        ...SALES_ORDER_MESSAGES.RETURN_PROCESS_ERROR(error.message),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseReturnModal = () => {
    setReturnModal({ open: false, orderId: null, orderCode: null, eligibility: null });
  };

  const handleCloseReturnConfirmation = () => {
    setReturnConfirmationModal({ open: false, refundSummary: null });
  };

  const handleCloseExchangeModal = () => {
    setExchangeModal({ open: false, order: null });
  };

  const handleExchangeCompleted = () => {
    setNotification({
      open: true,
      severity: 'success',
      message: 'Intercambio procesado con éxito.',
    });

    refreshOrdersAfterMutation();

    // Refresh order details modal if it's open for the same order
    if (orderDetailsModal.open && orderDetailsModal.orderId === exchangeModal.order?.id) {
      setOrderDetailsRefreshTrigger((prev: number) => prev + 1);
    }

    setExchangeModal({ open: false, order: null });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          <OrdersActionButtons onCreateOrder={handleCreateOrder} onExport={handleExport} />
        </ListPageToolbar>

        <OrdersSummaryBar
          counts={
            postSalesCounts ?? {
              total: 0,
              pendientesDePago: 0,
              completadas: 0,
              devueltas: 0,
              intercambios: 0,
            }
          }
          loading={postSalesLoading}
        />

        <ListPageFiltersSection>
          <OrdersFilters
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
            onReset={handleReset}
            locations={branchLocations}
            isLoading={isLoading}
            isLocationLocked={isLocationLocked}
            onLocationScopeChange={handleLocationScopeChange}
          />
        </ListPageFiltersSection>

        {/* Error message */}
        {fetchError && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Alert severity="error">{fetchError}</Alert>
          </Box>
        )}

        {/* Orders table - Opción 4: Sin Paper, borde y fondo sutil */}
        {!isLoading && orders.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'visible',
              minHeight: 0,
              px: 0,
            }}
          >
            <OrdersTable
              orders={orders}
              loading={isLoading}
              error={fetchError}
              selectedOrders={selectedOrders}
              setSelectedOrders={setSelectedOrders}
              expandedOrderIds={expandedOrderIds}
              onExpandedOrderIdsChange={setExpandedOrderIds}
              onViewOrder={handleViewOrder}
              onEditOrder={handleEditOrder}
              onDeliverOrder={handleDeliverOrder}
              onProcessPayment={handleProcessPayment}
              onReturnOrder={handleReturnOrder}
              onExchangeOrder={handleExchangeOrder}
              onEditSaleDate={handleOpenEditSaleDate}
            />
          </Box>
        )}

        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!isLoading && orders.length === 0 && !fetchError && (
          <EmptyStateMessage message="No se encontraron ventas" minHeight={200} />
        )}

        {/* Pagination - Opción 4: Sin Paper, sin borde superior */}
        {!isLoading && orders.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 25, 50, 100]} // Mantener opciones actuales
            />
          </Box>
        )}
      </ListPageMain>

      {/* Sales Modal */}
      <SalesModal />

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderDetailsModal.open}
        onClose={handleCloseOrderDetails}
        orderId={orderDetailsModal.orderId}
        onEditOrder={handleEditOrder}
        onProcessPayment={handleProcessPayment}
        onGenerateInstallments={handleOpenInstallments}
        refreshTrigger={orderDetailsRefreshTrigger}
        onOrdersMutated={refreshOrdersAfterMutation}
      />

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModal.open}
        onClose={handleClosePaymentModal}
        order={paymentModal.order}
        onPaymentProcessed={handlePaymentProcessed}
      />

      {/* Installments Modal - ⚠️ INACTIVO: No hay forma de abrirlo desde la UI */}
      <InstallmentsModal
        open={installmentsModal.open}
        onClose={handleCloseInstallmentsModal}
        order={installmentsModal.order}
        onInstallmentsUpdated={handleInstallmentsUpdated}
      />

      {/* Return Order Modal */}
      <ReturnOrderModal
        open={returnModal.open}
        onClose={handleCloseReturnModal}
        onSubmit={handleProcessReturn}
        eligibility={returnModal.eligibility}
        orderCode={returnModal.orderCode ?? undefined}
        loading={isLoading}
      />

      {/* Return Confirmation Modal */}
      <ReturnConfirmationModal
        open={returnConfirmationModal.open}
        onClose={handleCloseReturnConfirmation}
        refundSummary={returnConfirmationModal.refundSummary}
      />

      {/* Exchange Drawer */}
      <ExchangeDrawer
        open={exchangeModal.open}
        onClose={handleCloseExchangeModal}
        order={exchangeModal.order}
        onExchangeCompleted={handleExchangeCompleted}
      />

      {editSaleDateModal.order != null && (
        <EditSaleDateModal
          open={editSaleDateModal.open}
          onClose={handleCloseEditSaleDate}
          orderCode={editSaleDateModal.order.orderCode}
          saleDate={editSaleDateModal.order.saleDate}
          createdAt={editSaleDateModal.order.createdAt}
          loading={editSaleDateAction.loading}
          error={editSaleDateAction.error}
          onSubmit={handleSaveEditSaleDate}
        />
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'info' ? 3200 : 6000}
        onClose={handleCloseNotification}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: notification.severity === 'info' ? 'center' : 'right',
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            ...(notification.severity === 'info'
              ? {
                  boxShadow: '0 8px 24px rgba(6, 182, 212, 0.25)',
                  background: 'linear-gradient(to right, #06b6d4, #10b981)',
                  color: '#fff',
                  '& .MuiAlert-icon': { color: '#fff' },
                }
              : {}),
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};
export default SalesOrdersPage;
